import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Shuffle, Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchDictionary } from "@/lib/api";
import { toast } from "sonner";

// Fisher-Yates
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const truncate = (s, n) => (s.length <= n ? s : s.slice(0, n - 1) + "…");

const MatchGame = () => {
  const [terms, setTerms] = useState([]);
  const [round, setRound] = useState(0);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [matches, setMatches] = useState({}); // {term: defString}
  const [attempts, setAttempts] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [wrongPair, setWrongPair] = useState(null);

  useEffect(() => {
    fetchDictionary().then((d) => setTerms(d.terms || [])).catch(() => {});
  }, []);

  const roundTerms = useMemo(() => {
    if (!terms.length) return [];
    return shuffle(terms).slice(0, 6);
  }, [terms, round]);

  const shuffledDefs = useMemo(() => shuffle(roundTerms), [roundTerms]);

  const solved = Object.keys(matches).length;
  const total = roundTerms.length;
  const done = total > 0 && solved === total;

  const pickTerm = (t) => {
    if (matches[t.term]) return;
    setSelectedTerm(t);
  };

  const pickDef = (defTerm) => {
    if (!selectedTerm) return;
    setAttempts((n) => n + 1);
    if (defTerm.term === selectedTerm.term) {
      setMatches((m) => ({ ...m, [selectedTerm.term]: defTerm.definition }));
      setSelectedTerm(null);
    } else {
      setMistakes((n) => n + 1);
      setWrongPair({ term: selectedTerm.term, def: defTerm.term });
      setTimeout(() => setWrongPair(null), 450);
      setSelectedTerm(null);
    }
  };

  const nextRound = () => {
    setMatches({});
    setSelectedTerm(null);
    setAttempts(0);
    setMistakes(0);
    setRound((r) => r + 1);
  };

  useEffect(() => {
    if (done) {
      const acc = Math.max(0, Math.round(100 * (total / Math.max(attempts, total))));
      toast.success(`Round complete — ${total} matched with ${mistakes} miss${mistakes === 1 ? "" : "es"} · ${acc}% accuracy`);
    }
  }, [done]); // eslint-disable-line

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24" data-testid="game-page">
      <section className="bg-emerald text-cream py-14">
        <div className="max-w-[1100px] mx-auto px-6">
          <Reveal>
            <p className="eyebrow text-gold mb-3">Matching Game</p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">Match the term to its meaning.</h1>
            <p className="font-editorial italic text-cream/75 text-xl mt-4">
              Tap a term, then tap the matching definition. Six pairs per round.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4 text-sm font-body text-ink/70" data-testid="game-scoreboard">
            <span>Round <strong className="text-emerald">{round + 1}</strong></span>
            <span>Matched <strong className="text-emerald">{solved}/{total}</strong></span>
            <span>Misses <strong className={mistakes > 0 ? "text-red-600" : "text-emerald"}>{mistakes}</strong></span>
          </div>
          <button onClick={nextRound} className="btn-outline" data-testid="game-shuffle">
            <Shuffle className="w-4 h-4" /> New round
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Terms */}
          <div className="space-y-2">
            <p className="eyebrow text-emerald mb-2">Terms</p>
            {roundTerms.map((t) => {
              const done = !!matches[t.term];
              const active = selectedTerm?.term === t.term;
              const wrong = wrongPair?.term === t.term;
              return (
                <button
                  key={`t-${t.term}`}
                  onClick={() => pickTerm(t)}
                  disabled={done}
                  data-testid={`game-term-${t.term.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`w-full text-left p-4 border transition-all ${
                    done ? "bg-emerald text-cream border-emerald opacity-70"
                    : active ? "border-gold bg-gold/10 shadow-md"
                    : wrong ? "border-red-400 bg-red-50 animate-pulse"
                    : "border-line bg-surface hover:border-emerald/50"
                  }`}
                >
                  <span className="font-display text-lg">{t.term}</span>
                  {done && <Check className="w-4 h-4 inline ml-2 text-gold" />}
                </button>
              );
            })}
          </div>

          {/* Definitions (shuffled) */}
          <div className="space-y-2">
            <p className="eyebrow text-emerald mb-2">Definitions</p>
            {shuffledDefs.map((t) => {
              const isMatched = !!matches[t.term];
              const wrong = wrongPair?.def === t.term;
              return (
                <button
                  key={`d-${t.term}`}
                  onClick={() => pickDef(t)}
                  disabled={isMatched || !selectedTerm}
                  data-testid={`game-def-${t.term.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`w-full text-left p-4 border transition-all ${
                    isMatched ? "bg-emerald text-cream border-emerald opacity-70"
                    : wrong ? "border-red-400 bg-red-50 animate-pulse"
                    : !selectedTerm ? "border-line bg-surface opacity-70 cursor-not-allowed"
                    : "border-line bg-surface hover:border-gold"
                  }`}
                >
                  <span className="font-editorial italic text-sm">{truncate(t.definition, 140)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {done && (
          <div className="mt-10 p-8 text-center border border-gold bg-gold/10" data-testid="game-round-done">
            <p className="eyebrow text-emerald mb-2">Round complete</p>
            <p className="font-display text-3xl text-emerald mb-4">Beautifully done.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={nextRound} className="btn-emerald">
                Next round <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/dictionary" className="btn-outline">Browse the Dictionary</Link>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default MatchGame;
